import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, ChartLine, CircleNotch } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ChartWatermark } from "@/components/FarmwatchLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function GlobalPriceHistoryModal({ 
  isOpen, 
  onClose, 
  country,
  marketName, 
  category,
  currentPrice,
  currency,
  unit 
}) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(90);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { days: period };
      if (category) params.category = category;
      
      const response = await axios.get(
        `${API}/global/history/${country}/${encodeURIComponent(marketName)}`,
        { params }
      );
      
      // Ensure the last data point uses the current price from the table
      const historyData = response.data;
      if (currentPrice && historyData.history && historyData.history.length > 0) {
        historyData.history[historyData.history.length - 1].price = currentPrice;
        historyData.stats.current = currentPrice;
      }
      
      setData(historyData);
    } catch (err) {
      console.error("Error fetching history:", err);
      setError("Failed to load price history");
    } finally {
      setLoading(false);
    }
  }, [marketName, category, period, country, currentPrice]);

  useEffect(() => {
    if (isOpen && marketName) {
      fetchHistory();
    }
  }, [isOpen, marketName, fetchHistory]);

  if (!isOpen) return null;

  const periodOptions = [
    { value: 30, label: "30D" },
    { value: 60, label: "60D" },
    { value: 90, label: "90D" },
    { value: 180, label: "180D" },
    { value: 365, label: "1Y" },
  ];

  const formatCurrency = (value) => {
    if (currency === "BRL") return `R$${value}`;
    if (currency === "EUR") return `€${value}`;
    if (currency === "GBP") return `${value}p`;
    if (currency === "USD") return `$${value}`;
    if (currency === "INR") return `₹${value}`;
    if (currency === "MXN") return `$${value}`;
    if (currency === "CNY") return `¥${value}`;
    return value;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#111827] border border-[#1F2937] w-full max-w-4xl max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#1F2937]">
          <div className="flex items-center gap-3">
            <ChartLine size={24} className="text-[#0F766E]" />
            <div>
              <h2 className="text-lg font-serif text-white">{marketName}</h2>
              <p className="text-xs text-[#8B8580]">
                {category ? category.replace(/_/g, " ").toUpperCase() : "Price History"} • {unit}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1F2937] transition-colors">
            <X size={20} className="text-[#8B8580]" />
          </button>
        </div>

        {/* Period Selector */}
        <div className="flex items-center gap-2 p-4 border-b border-[#1F2937]">
          <span className="text-xs text-[#6B6661] mr-2">Period:</span>
          {periodOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-3 py-1 text-xs font-mono transition-colors ${
                period === opt.value
                  ? "bg-[#0F766E] text-white"
                  : "bg-[#1F2937] text-[#8B8580] hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <CircleNotch size={40} className="animate-spin text-[#0F766E]" />
            </div>
          ) : error ? (
            <div className="text-center py-20 text-[#DC2626]">{error}</div>
          ) : data ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">Current</span>
                  <span className="font-mono text-lg text-white">{formatCurrency(currentPrice || data.stats.current)}</span>
                </div>
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">Average</span>
                  <span className="font-mono text-lg text-[#8B8580]">{formatCurrency(data.stats.average)}</span>
                </div>
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">High</span>
                  <span className="font-mono text-lg text-[#059669]">{formatCurrency(data.stats.max)}</span>
                </div>
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">Low</span>
                  <span className="font-mono text-lg text-[#DC2626]">{formatCurrency(data.stats.min)}</span>
                </div>
                <div className="bg-[#1F2937] p-3">
                  <span className="text-xs text-[#6B6661] block">30D Change</span>
                  <span className={`font-mono text-lg ${data.stats.change_30d >= 0 ? "text-[#059669]" : "text-[#DC2626]"}`}>
                    {data.stats.change_30d >= 0 ? "+" : ""}{data.stats.change_30d_percent}%
                  </span>
                </div>
              </div>

              {/* Chart */}
              <div className="h-[300px] relative">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6B7280"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(val) => {
                        const d = new Date(val);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                      }}
                    />
                    <YAxis 
                      stroke="#6B7280"
                      tick={{ fontSize: 10 }}
                      domain={['auto', 'auto']}
                      tickFormatter={(val) => formatCurrency(val)}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "#1F2937", 
                        border: "1px solid #374151",
                        borderRadius: 0
                      }}
                      labelStyle={{ color: "#9CA3AF" }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        return (
                          <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', padding: '10px' }}>
                            <p style={{ color: '#9CA3AF', marginBottom: '8px' }}>
                              {new Date(label).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                            {payload.map((entry, index) => {
                              // Use white for Price, keep original colors for others
                              const color = entry.name === "Price" ? "#FFFFFF" : entry.color;
                              let displayName = entry.name;
                              let displayValue = formatCurrency(entry.value);
                              if (entry.name === "usd_price") {
                                displayName = "USD Equiv.";
                                displayValue = `$${entry.value}`;
                              } else if (entry.name === "ma_7") {
                                displayName = "7D MA";
                              } else if (entry.name === "ma_30") {
                                displayName = "30D MA";
                              }
                              return (
                                <p key={index} style={{ color, margin: '4px 0' }}>
                                  {displayName}: {displayValue}
                                </p>
                              );
                            })}
                          </div>
                        );
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#0F766E" 
                      strokeWidth={2}
                      dot={false}
                      name="Price"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ma_7" 
                      stroke="#10B981" 
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      dot={false}
                      name="7D MA"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="ma_30" 
                      stroke="#064E3B" 
                      strokeWidth={1}
                      strokeDasharray="5 5"
                      dot={false}
                      name="30D MA"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <ChartWatermark position="bottom-right" size="small" />
              </div>

              {/* USD Equivalent Note */}
              <div className="mt-4 text-center">
                <span className="text-xs text-[#6B6661]">
                  USD Equivalent: ${data.history[data.history.length - 1]?.usd_price || "N/A"} (current)
                </span>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
